import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, User, Bot, Loader2, RotateCcw, BookOpen, Sparkles, ChefHat } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import PinchePanel from '@/components/copilot/PinchePanel';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const WEBHOOK_URL = 'https://n8n.pleko.app/webhook/support-chat';

// Map routes to their tour localStorage keys
const ROUTE_TOURS: Record<string, string[]> = {
  '/dashboard': ['pleko-tour-dashboard', 'pleko-checklist-dismissed'],
  '/ingredients': ['pleko-tour-ingredients'],
  '/recipes': ['pleko-tour-recipes'],
  '/cartas': ['pleko-tour-cartas'],
  '/stock': ['pleko-tour-stock'],
};

function getGuideLabel(pathname: string): string {
  if (pathname.startsWith('/ingredients')) return 'Ingredientes';
  if (pathname.startsWith('/recipes')) return 'Recetas';
  if (pathname.startsWith('/cartas')) return 'Cartas';
  if (pathname.startsWith('/stock')) return 'Almacén';
  if (pathname.startsWith('/dashboard')) return 'Dashboard';
  return 'esta página';
}

function getTourKeysForRoute(pathname: string): string[] {
  for (const [route, keys] of Object.entries(ROUTE_TOURS)) {
    if (pathname === route || pathname.startsWith(route + '/')) {
      return keys;
    }
  }
  return ['pleko-tour-dashboard', 'pleko-checklist-dismissed'];
}

type View = 'menu' | 'chat';

export default function SupportChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [isPincheOpen, setIsPincheOpen] = useState(false);
  const [view, setView] = useState<View>('menu');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { pathname } = useLocation();
  const { user, currentRestaurantId, restaurants } = useAuthStore();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && view === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, view]);

  // Reset to menu when closing
  useEffect(() => {
    if (!isOpen) {
      setView('menu');
    }
  }, [isOpen]);

  const isAdmin = user?.is_admin === true;

  if (pathname === '/ayuda') return null;

  const currentRestaurant = restaurants.find(r => r.id === currentRestaurantId);
  const guideLabel = getGuideLabel(pathname);

  const handleResetGuide = () => {
    const keys = getTourKeysForRoute(pathname);
    keys.forEach((key) => localStorage.removeItem(key));
    setIsOpen(false);
    window.location.reload();
  };

  const openChat = () => {
    setView('chat');
  };

  const handleOpenPinche = () => {
    if (!isAdmin) return;
    setIsOpen(false);
    setIsPincheOpen(true);
  };

  const handleFabClick = () => {
    if (isPincheOpen) {
      setIsPincheOpen(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const sendMessage = async (text?: string) => {
    const msg = text || input.trim();
    if (!msg || isLoading) return;

    if (view !== 'chat') setView('chat');

    const userMessage: ChatMessage = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          page: pathname,
          session_id: sessionId,
          history: [...messages, userMessage].slice(-10),
          user: user ? {
            name: user.name,
            email: user.email,
            plan: user.on_trial ? 'trial' : 'subscribed',
            trial_days: user.trial_days_remaining,
            restaurant: currentRestaurant?.name || null,
          } : null,
        }),
      });

      const data = await response.json();
      const replyContent = data.escalated
        ? `${data.reply}\n\nTe he puesto en contacto con el equipo. También puedes escribirnos directamente por WhatsApp.`
        : data.reply || 'Lo siento, ha habido un error. Intenta de nuevo.';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: replyContent,
      }]);
      if (data.escalated) {
        openWhatsApp(msg);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'No he podido conectar con el servidor. Intenta de nuevo en unos segundos.',
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const WHATSAPP_NUMBER = '34682321733';

  const openWhatsApp = (context?: string) => {
    const msg = context
      ? `Hola, necesito ayuda con PLEKO. ${context}`
      : 'Hola, necesito ayuda con PLEKO.';
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleEscalate = () => {
    const lastMsg = messages.filter(m => m.role === 'user').pop()?.content;
    openWhatsApp(lastMsg);
    sendMessage('Quiero hablar con un humano');
  };

  return (
    <>
      {/* El Pinche IA — Slide-over panel */}
      <PinchePanel isOpen={isPincheOpen} onClose={() => setIsPincheOpen(false)} />

      {/* Help Menu Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">

          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 text-white shrink-0"
            style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}
          >
            <div className="flex items-center gap-2">
              <ChefHat className="w-5 h-5" />
              <div>
                <div className="font-semibold text-sm">
                  {view === 'chat' ? 'Soporte PLEKO' : '¿En qué podemos ayudarte?'}
                </div>
                <div className="text-xs text-orange-100">
                  {view === 'chat' ? 'Normalmente responde al instante' : 'El Pinche IA y más'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {view === 'chat' && (
                <button onClick={() => setView('menu')} className="p-1.5 hover:bg-white/20 rounded-full transition-colors text-xs">
                  ←
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Menu View */}
          {view === 'menu' && (
            <div className="p-4 space-y-2">
              {/* El Pinche IA — Only for super admins (testing) */}
              {isAdmin && (
                <button
                  onClick={handleOpenPinche}
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors border border-orange-200 relative"
                  style={{ background: 'linear-gradient(135deg, #FFF7ED, #FFEDD5)' }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #F97316, #EA580C)' }}
                  >
                    <ChefHat className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-sm text-slate-800 block">El Pinche IA</span>
                    <span className="text-xs text-orange-600">Pregunta sobre costes, recetas, stock...</span>
                  </div>
                  <span className="absolute top-2 right-2 text-[9px] font-bold text-white px-1.5 py-0.5 rounded-full" style={{ background: '#F97316' }}>
                    BETA
                  </span>
                </button>
              )}

              {/* Reiniciar guía */}
              <button
                onClick={handleResetGuide}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left hover:bg-blue-50 transition-colors border border-slate-100"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <RotateCcw className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <span className="font-semibold text-sm text-slate-800 block">Reiniciar guía</span>
                  <span className="text-xs text-slate-400">Guía de {guideLabel}</span>
                </div>
              </button>

              {/* Reconfigurar wizard */}
              <button
                onClick={() => {
                  localStorage.removeItem('pleko-onboarding-done');
                  localStorage.removeItem('pleko-onboarding-profile');
                  localStorage.removeItem('pleko-flowbar-dismissed');
                  localStorage.removeItem('pleko-checklist-dismissed');
                  setIsOpen(false);
                  window.location.href = '/dashboard';
                }}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left hover:bg-amber-50 transition-colors border border-slate-100"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <span className="font-semibold text-sm text-slate-800 block">Configurar mi ruta</span>
                  <span className="text-xs text-slate-400">Repetir las 3 preguntas iniciales</span>
                </div>
              </button>

              {/* Chatbot / Soporte */}
              <button
                onClick={openChat}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left hover:bg-green-50 transition-colors border border-slate-100"
              >
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <span className="font-semibold text-sm text-slate-800 block">Contactar soporte</span>
                  <span className="text-xs text-slate-400">Chatbot + WhatsApp</span>
                </div>
              </button>

              {/* Video tutoriales */}
              <Link
                to="/ayuda"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left hover:bg-purple-50 transition-colors border border-slate-100"
              >
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <span className="font-semibold text-sm text-slate-800 block">Video tutoriales</span>
                  <span className="text-xs text-slate-400">22 vídeos paso a paso</span>
                </div>
              </Link>
            </div>
          )}

          {/* Chat View */}
          {view === 'chat' && (
            <>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 h-[400px]">
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <Bot className="w-12 h-12 mx-auto text-blue-500 mb-3" />
                    <p className="text-gray-700 font-medium text-sm">Hola{user ? `, ${user.name.split(' ')[0]}` : ''}!</p>
                    <p className="text-gray-500 text-xs mt-1">Pregunta lo que necesites sobre PLEKO</p>
                    <div className="mt-4 space-y-2">
                      {[
                        '¿Cómo creo un escandallo?',
                        '¿Qué incluye cada plan?',
                        '¿Cómo escaneo una factura?',
                      ].map((q) => (
                        <button
                          key={q}
                          onClick={() => sendMessage(q)}
                          className="block w-full text-left text-xs px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0 mt-1">
                        <Bot className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-blue-500 text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}
                    >
                      {msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center shrink-0 mt-1">
                        <User className="w-3.5 h-3.5 text-gray-600" />
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="bg-gray-100 px-4 py-2 rounded-2xl rounded-bl-md">
                      <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-gray-200 px-3 py-2 shrink-0">
                <form
                  onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                  className="flex items-center gap-2"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Escribe tu pregunta..."
                    className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-full focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                    disabled={isLoading}
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="w-8 h-8 flex items-center justify-center bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
                <button
                  onClick={handleEscalate}
                  className="w-full mt-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Hablar con un humano
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Floating Button — El Pinche */}
      <button
        onClick={handleFabClick}
        className="pinche-fab fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center text-white hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}
        aria-label="El Pinche IA"
      >
        {(isOpen || isPincheOpen) ? (
          <X className="w-6 h-6" />
        ) : (
          <ChefHat className="w-7 h-7 pinche-icon" />
        )}
      </button>

      {/* FAB animation styles */}
      <style>{`
        .pinche-fab {
          animation: pinche-float 3s ease-in-out infinite;
        }
        .pinche-fab::before {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          background: linear-gradient(135deg, #F97316, #EA580C);
          opacity: 0;
          animation: pinche-ping 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
          z-index: -1;
        }
        .pinche-fab:hover {
          animation: none;
        }
        .pinche-icon {
          animation: pinche-wiggle 4s ease-in-out infinite;
        }
        @keyframes pinche-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes pinche-ping {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes pinche-wiggle {
          0%, 85%, 100% { transform: rotate(0deg); }
          88% { transform: rotate(-8deg); }
          91% { transform: rotate(8deg); }
          94% { transform: rotate(-5deg); }
          97% { transform: rotate(5deg); }
        }
      `}</style>
    </>
  );
}
